import React, { createContext, useContext } from 'react';

import { tv } from '../../tv';

/**
 * A heading.
 *
 * Separate from Text because visual size and document level are independent:
 * a section heading may need to look small and still be an h2. The product
 * already ran into this, writing a page title as a Text with role and
 * aria-level attached to fake the semantics.
 *
 * The element is derived rather than asked for. A caller picks how the
 * heading should look; the level follows from where the heading sits, which
 * is what keeps the document outline correct without every call site
 * reasoning about it. `as` overrides when a design genuinely needs it.
 */

/** The level a nested heading should take, one deeper than its enclosing section. */
const HeadingLevelContext = createContext<1 | 2 | 3 | 4 | 5 | 6>(1);

/**
 * Marks a region whose headings sit one level deeper. Wrapping a card or a
 * panel means its heading becomes an h3 under an h2 without the caller
 * counting levels.
 */
export const HeadingLevel = ({ children }: { children?: React.ReactNode }) => {
  const level = useContext(HeadingLevelContext);
  const next = Math.min(level + 1, 6) as 1 | 2 | 3 | 4 | 5 | 6;
  return <HeadingLevelContext.Provider value={next}>{children}</HeadingLevelContext.Provider>;
};

const headingStyles = tv({
  base: 'ck:text-text-default ck:font-semibold',
  variants: {
    size: {
      /** Page titles. */
      xl: 'ck:text-3xl',
      lg: 'ck:text-2xl',
      md: 'ck:text-xl',
      sm: 'ck:text-base',
      xs: 'ck:text-sm',
    },
    color: {
      default: 'ck:text-text-default',
      muted: 'ck:text-text-muted',
      inherit: 'ck:text-inherit',
    },
    truncated: {
      true: 'ck:truncate',
      false: '',
    },
    /* Alignment is a property of the heading's text, not of a box around it
     * (the same ruling as Text), so a centred card title needs no wrapper. */
    align: {
      start: 'ck:text-left',
      center: 'ck:text-center',
      end: 'ck:text-right',
    },
  },
  defaultVariants: { size: 'md', color: 'default', truncated: false },
});

export interface HeadingProps {
  children?: React.ReactNode;
  /**
   * How the heading looks. Required: a heading with no chosen size is a
   * decision nobody made, and the sizes carry the product's real hierarchy.
   */
  size: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  color?: 'default' | 'muted' | 'inherit';
  /** Truncates to a single line with an ellipsis, for a long title. */
  isTruncated?: boolean;
  /** Aligns the heading's text within its own box. */
  align?: 'start' | 'center' | 'end';
  /**
   * Overrides the derived element. Use when the outline genuinely differs
   * from the nesting, not to change how the heading looks.
   */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  id?: string;
}

export const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(function Heading(
  { children, size, color = 'default', as, isTruncated = false, align, ...props },
  ref,
) {
  const level = useContext(HeadingLevelContext);
  const As = as ?? (`h${level}` as const);

  return (
    <As
      {...props}
      ref={ref}
      className={headingStyles({ size, color, truncated: isTruncated, align })}
      data-component="heading"
    >
      {children}
    </As>
  );
});
